// src/context/SellRequestContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import { sellCarService, inspectionService } from '../services/sellCarService';

const SellRequestContext = createContext();

export const useSellRequest = () => {
  const context = useContext(SellRequestContext);
  if (!context) {
    throw new Error('useSellRequest must be used within SellRequestProvider');
  }
  return context;
};

export const SellRequestProvider = ({ children }) => {
  const [sellRequests, setSellRequests] = useState([]);
  const [currentSellRequest, setCurrentSellRequest] = useState(null);
  const [currentInspection, setCurrentInspection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Clear messages
  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  // Submit car details
  const submitCarDetails = useCallback(async (formData) => {
    setLoading(true);
    clearMessages();
    try {
      const response = await sellCarService.submitCarDetails(formData);
      if (response.success) {
        setCurrentSellRequest(response.data);
        setSuccess('Car details submitted successfully!');
        return response.data;
      } else {
        throw new Error(response.errors?.[0] || 'Failed to submit car details');
      }
    } catch (err) {
      const errorMsg = err.errors?.[0] || err.message || 'Failed to submit car details';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearMessages]);

  // Book inspection
  const bookInspection = useCallback(async (inspectionData) => {
    setLoading(true);
    clearMessages();
    try {
      const response = await inspectionService.bookInspection(inspectionData);
      if (response.status === 'success') {
        setCurrentInspection(response.data);
        setSuccess('Inspection booked successfully!');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to book inspection');
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to book inspection';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearMessages]);

  // Fetch all sell requests
  const fetchSellRequests = useCallback(async () => {
    setLoading(true);
    clearMessages();
    try {
      const response = await sellCarService.getSellRequests();
      setSellRequests(response);
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch sell requests';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearMessages]);

  // Fetch specific sell request
  const fetchSellRequest = useCallback(async (id) => {
    setLoading(true);
    clearMessages();
    try {
      const response = await sellCarService.getSellRequest(id);
      setCurrentSellRequest(response);
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch sell request';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearMessages]);

  // Fetch all inspections
  const fetchInspections = useCallback(async () => {
    setLoading(true);
    clearMessages();
    try {
      const response = await inspectionService.getInspections();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch inspections';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearMessages]);

  // Fetch specific inspection
  const fetchInspection = useCallback(async (id) => {
    setLoading(true);
    clearMessages();
    try {
      const response = await inspectionService.getInspection(id);
      setCurrentInspection(response);
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch inspection';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearMessages]);

  // Update sell request
  const updateSellRequest = useCallback(async (id, data) => {
    setLoading(true);
    clearMessages();
    try {
      const response = await sellCarService.updateSellRequest(id, data);
      if (response.success) {
        setCurrentSellRequest(response.data);
        setSuccess('Sell request updated successfully!');
        return response.data;
      } else {
        throw new Error(response.errors?.[0] || 'Failed to update sell request');
      }
    } catch (err) {
      const errorMsg = err.errors?.[0] || err.message || 'Failed to update sell request';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearMessages]);

  // Cancel inspection
  const cancelInspection = useCallback(async (id) => {
    setLoading(true);
    clearMessages();
    try {
      const response = await inspectionService.cancelInspection(id);
      if (response.success) {
        setCurrentInspection(null);
        setSuccess('Inspection cancelled successfully!');
        return response;
      } else {
        throw new Error(response.message || 'Failed to cancel inspection');
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to cancel inspection';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearMessages]);

  // Delete sell request
  const deleteSellRequest = useCallback(async (id) => {
    setLoading(true);
    clearMessages();
    try {
      const response = await sellCarService.deleteSellRequest(id);
      if (response.success) {
        setSellRequests(prev => prev.filter(req => req.id !== id));
        setCurrentSellRequest(null);
        setSuccess('Sell request cancelled successfully!');
        return response;
      } else {
        throw new Error(response.message || 'Failed to delete sell request');
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to delete sell request';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearMessages]);

  // Reset form
  const resetForm = useCallback(() => {
    setCurrentSellRequest(null);
    setCurrentInspection(null);
    clearMessages();
  }, [clearMessages]);

  const value = {
    // State
    sellRequests,
    currentSellRequest,
    currentInspection,
    loading,
    error,
    success,
    
    // Actions
    submitCarDetails,
    bookInspection,
    fetchSellRequests,
    fetchSellRequest,
    fetchInspections,
    fetchInspection,
    updateSellRequest,
    cancelInspection,
    deleteSellRequest,
    resetForm,
    clearMessages
  };

  return (
    <SellRequestContext.Provider value={value}>
      {children}
    </SellRequestContext.Provider>
  );
};

export default SellRequestContext;